import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NativeSyntheticEvent, Platform, View } from 'react-native';

import {
  EmojiPicker,
  EmojiPickerRefType,
  TextInput,
} from '@components';
import { useColors } from '@hooks';
import { AnalyticsService, ToastService } from '@services';
import { IS_WEB, parseApiError, passIf } from '@utils';
import { useChatsModel } from '@models/Chats/ChatsModel';

const Container = styled(View)({
  px: ['$6', '$0'],
});

const EmojiPickerContainer = styled(View)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const MessageInput = styled(TextInput)({
  height: 'auto',
});

interface IMessagingFooterProps {
  conversationId: number;
}

export const MessagingFooter = observer<IMessagingFooterProps>(
  (props) => {
    const [inputValue, setInputValue] = useState('');
    const emojiPickerRef = useRef<EmojiPickerRefType>(null);
    const { t } = useTranslation();
    const colors = useColors();
    const chats = useChatsModel();
    const conversation = chats.getById(props.conversationId);

    async function sendMessage() {
      if (isInputValue) {
        const message = inputValue;

        try {
          setInputValue('');

          await conversation?.sendMessage.run({
            message,
          });
          AnalyticsService.trackChatMessage();
        } catch (error) {
          ToastService.showError(parseApiError(error).message);
        }
      }
    }

    function selectEmoji(symbol: string) {
      setInputValue(inputValue + symbol);
    }

    function toggleEmojiPicker() {
      emojiPickerRef.current?.toggle();
    }

    function handleEnterKey(e: NativeSyntheticEvent<any>) {
      // For WEB, when pressing Enter key, we need to submit message
      // but not for Shift + Enter key combination
      if (IS_WEB && isInputValue) {
        const isEnterPressed =
          e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey;

        if (isEnterPressed) {
          e.nativeEvent.preventDefault();
          sendMessage();
        }
      }
    }

    const isInputValue = !!inputValue.trim();

    return (
      <Container>
        <MessageInput
          value={inputValue}
          onChangeText={setInputValue}
          onKeyPress={handleEnterKey}
          placeholderTextColor={colors.$text60}
          placeholder={t('messaging.messagePlaceholder')}
          iconRight={{ name: 'send-light', onPress: sendMessage }}
          iconLeft={Platform.select({
            web: { name: 'smile', onPress: toggleEmojiPicker },
          })}
          maxLength={1000}
          maxHeight={300}
          multiline
          // On the web this value defaults to 2, showing 2 lines of text
          // This change ensures it matches the design by displaying 1 line of text instead
          numberOfLines={passIf(IS_WEB, 1)}
        />
        <EmojiPickerContainer>
          <EmojiPicker
            pickerRef={emojiPickerRef}
            onSelect={selectEmoji}
            withIcon={false}
          />
        </EmojiPickerContainer>
      </Container>
    );
  },
);
